/**
 * ProfileEntrepriseIdentiteController — onglet « Identité légale »
 * de /profil-entreprise.html (EPIC-099 / 99.15).
 *
 * Pilote 2 nm-edit-block indépendants :
 *  - `block-identite`    : raison sociale, dénomination commerciale,
 *                          mentions RCS (BIC vente / BIC services non
 *                          artisanal) et RM (BIC services artisanal).
 *  - `block-coordonnees` : adresse, téléphone et email professionnels.
 *
 * Comble un gap d'EPIC-007 / 7.7 où les boutons « Modifier » étaient
 * `disabled` faute d'endpoints backend.
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : les résumés sont rendus depuis
 *     <template> (`tpl-identite-summary-*` / `tpl-coordonnees-summary-*`)
 *     clonés via cloneTemplate / fillSlots.
 *   - Vocab UI : « PA » côté utilisateur (jamais « PDP »), NotaFacto et
 *     Sweego invisibles.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class ProfileEntrepriseIdentiteController extends BaseController {
  static targets = [
    "blockIdentite",
    "blockCoordonnees",
    "identiteSummary",
    "coordonneesSummary",
    "legalNameInput",
    "commercialNameInput",
    "rcsField",
    "rmField",
    "rcsCityInput",
    "rmDepartmentInput",
    "addressLine1Input",
    "addressPostalCodeInput",
    "addressCityInput",
    "phoneInput",
    "emailProInput",
    "websiteUrlInput",
    "identiteSubmit",
    "coordonneesSubmit",
    "identiteError",
    "coordonneesError",
  ];

  state = {
    identity: {
      legalName: null,
      commercialName: null,
      rcsCity: null,
      rmDepartment: null,
      rcsCityRequired: false,
      rmDepartmentRequired: false,
    },
    contact: {
      addressLine1: null,
      addressPostalCode: null,
      addressCity: null,
      phone: null,
      emailPro: null,
      websiteUrl: null,
    },
  };

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  async connect() {
    await this._refreshFromApi();
    this._applyHashFocus();
    // §99.35 — re-évaluer les flags RCS/RM dès qu'une activité est créée,
    // modifiée ou archivée sans rechargement de page.
    this._onActivitiesUpdated = () => { this._refreshFromApi(); };
    document.addEventListener("activities:updated", this._onActivitiesUpdated);
    // §99.35 — quand l'utilisateur clique le CTA d'une alerte dashboard
    // (`/profil-entreprise.html#block-identite-{rcs,rm}-focus`), ouvrir le
    // form Identité légale et focuser le bon champ.
    this._onHashChange = () => { this._applyHashFocus(); };
    window.addEventListener("hashchange", this._onHashChange);
  }

  disconnect() {
    if (this._onActivitiesUpdated) {
      document.removeEventListener("activities:updated", this._onActivitiesUpdated);
    }
    if (this._onHashChange) {
      window.removeEventListener("hashchange", this._onHashChange);
    }
  }

  async _refreshFromApi() {
    try {
      const data = await api.getOrganizationProfile();
      if (data && data.identity) this._hydrateIdentity(data.identity);
      if (data && data.contact) this._hydrateContact(data.contact);
    } catch (error) {
      // Hydratation silencieuse — l'utilisateur peut toujours saisir.
      console.warn("[profile-entreprise-identite] hydration failed", error);
    }
  }

  _applyHashFocus() {
    const FOCUS_TARGETS = {
      "block-identite-rcs-focus": { inputTarget: "rcsCityInput" },
      "block-identite-rm-focus": { inputTarget: "rmDepartmentInput" },
    };
    const hash = window.location.hash ? window.location.hash.slice(1) : "";
    const focus = FOCUS_TARGETS[hash];
    if (!focus) return;
    if (!this.hasBlockIdentiteTarget) return;
    this._restoreIdentiteInputs();
    this._hideError("identite");
    this._open(this.blockIdentiteTarget);
    const hasInputTarget = focus.inputTarget === "rcsCityInput"
      ? this.hasRcsCityInputTarget
      : this.hasRmDepartmentInputTarget;
    if (!hasInputTarget) return;
    const input = focus.inputTarget === "rcsCityInput"
      ? this.rcsCityInputTarget
      : this.rmDepartmentInputTarget;
    // requestAnimationFrame : laisse `_open()` faire son focus initial sur
    // le premier champ, puis ré-aligne le focus sur celui qui correspond à
    // l'alerte cliquée.
    requestAnimationFrame(() => input.focus());
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel
  // ---------------------------------------------------------------------------

  openIdentite(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreIdentiteInputs();
    this._hideError("identite");
    this._open(block);
  }

  openCoordonnees(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreCoordonneesInputs();
    this._hideError("coordonnees");
    this._open(block);
  }

  cancelIdentite(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreIdentiteInputs();
    this._hideError("identite");
    this._close(block);
  }

  cancelCoordonnees(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreCoordonneesInputs();
    this._hideError("coordonnees");
    this._close(block);
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submitIdentite(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const form = event.target;
    if (form && typeof form.checkValidity === "function" && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const legalName = this._readTrim("legalNameInput");
    if (legalName === "") {
      this._showError("identite", "La raison sociale ne peut pas être vide.");
      return;
    }

    const payload = {
      legalName,
      commercialName: this._readNullable("commercialNameInput"),
      rcsCity: this._readNullable("rcsCityInput"),
      rmDepartment: this._readNullable("rmDepartmentInput"),
    };

    if (this.hasIdentiteSubmitTarget) this.identiteSubmitTarget.disabled = true;
    try {
      const data = await api.updateOrganizationIdentity(payload);
      if (data && data.identity) this._hydrateIdentity(data.identity);
      const block = this.hasBlockIdentiteTarget ? this.blockIdentiteTarget : null;
      if (block) this._close(block);
      this.showSuccess("Identité légale enregistrée.");
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la mise à jour de l'identité légale.";
      this._showError("identite", msg);
    } finally {
      if (this.hasIdentiteSubmitTarget) this.identiteSubmitTarget.disabled = false;
    }
  }

  async submitCoordonnees(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const payload = {
      addressLine1: this._readNullable("addressLine1Input"),
      addressPostalCode: this._readNullable("addressPostalCodeInput"),
      addressCity: this._readNullable("addressCityInput"),
      phone: this._readNullable("phoneInput"),
      emailPro: this._readNullable("emailProInput"),
      websiteUrl: this._readNullable("websiteUrlInput"),
    };

    if (this.hasCoordonneesSubmitTarget) this.coordonneesSubmitTarget.disabled = true;
    try {
      const data = await api.updateOrganizationContact(payload);
      if (data && data.contact) this._hydrateContact(data.contact);
      const block = this.hasBlockCoordonneesTarget ? this.blockCoordonneesTarget : null;
      if (block) this._close(block);
      this.showSuccess("Coordonnées professionnelles enregistrées.");
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la mise à jour des coordonnées.";
      this._showError("coordonnees", msg);
    } finally {
      if (this.hasCoordonneesSubmitTarget) this.coordonneesSubmitTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Hydration + render
  // ---------------------------------------------------------------------------

  _hydrateIdentity(identity) {
    this.state.identity = {
      legalName: identity.legalName ?? null,
      commercialName: identity.commercialName ?? null,
      rcsCity: identity.rcsCity ?? null,
      rmDepartment: identity.rmDepartment ?? null,
      rcsCityRequired: identity.rcsCityRequired === true,
      rmDepartmentRequired: identity.rmDepartmentRequired === true,
    };
    this._applyIdentiteFieldVisibility();
    this._renderIdentiteSummary();
    this._restoreIdentiteInputs();
  }

  _applyIdentiteFieldVisibility() {
    const { rcsCityRequired, rmDepartmentRequired } = this.state.identity;
    if (this.hasRcsFieldTarget) this.rcsFieldTarget.hidden = !rcsCityRequired;
    if (this.hasRmFieldTarget) this.rmFieldTarget.hidden = !rmDepartmentRequired;
    if (this.hasRcsCityInputTarget) {
      if (rcsCityRequired) {
        this.rcsCityInputTarget.setAttribute("required", "");
      } else {
        this.rcsCityInputTarget.removeAttribute("required");
      }
    }
    if (this.hasRmDepartmentInputTarget) {
      if (rmDepartmentRequired) {
        this.rmDepartmentInputTarget.setAttribute("required", "");
      } else {
        this.rmDepartmentInputTarget.removeAttribute("required");
      }
    }
  }

  _hydrateContact(contact) {
    this.state.contact = {
      addressLine1: contact.addressLine1 ?? null,
      addressPostalCode: contact.addressPostalCode ?? null,
      addressCity: contact.addressCity ?? null,
      phone: contact.phone ?? null,
      emailPro: contact.emailPro ?? null,
      websiteUrl: contact.websiteUrl ?? null,
    };
    this._renderCoordonneesSummary();
    this._restoreCoordonneesInputs();
  }

  _renderIdentiteSummary() {
    if (!this.hasIdentiteSummaryTarget) return;
    const { legalName, commercialName, rcsCity, rmDepartment } = this.state.identity;
    const isEmpty = !legalName && !commercialName && !rcsCity && !rmDepartment;
    const templateId = isEmpty
      ? "tpl-identite-summary-empty"
      : "tpl-identite-summary-filled";
    const fragment = this.cloneTemplate(templateId);
    if (!isEmpty) {
      this.fillSlots(fragment, {
        legalName: legalName ?? "—",
        commercialName: commercialName ?? "—",
        rcsCity: rcsCity ?? "—",
        rmDepartment: rmDepartment ?? "—",
      });
      const rcsChunk = fragment.querySelector('[data-summary-chunk="rcs"]');
      if (rcsChunk) rcsChunk.hidden = !rcsCity;
      const rmChunk = fragment.querySelector('[data-summary-chunk="rm"]');
      if (rmChunk) rmChunk.hidden = !rmDepartment;
    }
    this._replaceChildren(this.identiteSummaryTarget, fragment);
  }

  _renderCoordonneesSummary() {
    if (!this.hasCoordonneesSummaryTarget) return;
    const { addressLine1, addressPostalCode, addressCity, phone, emailPro } = this.state.contact;
    const isEmpty = !addressLine1 && !addressPostalCode && !addressCity && !phone && !emailPro;
    const templateId = isEmpty
      ? "tpl-coordonnees-summary-empty"
      : "tpl-coordonnees-summary-filled";
    const fragment = this.cloneTemplate(templateId);
    if (!isEmpty) {
      const addressParts = [addressLine1, [addressPostalCode, addressCity].filter(Boolean).join(" ")]
        .filter((v) => v && v.trim() !== "");
      this.fillSlots(fragment, {
        address: addressParts.length > 0 ? addressParts.join(" · ") : "—",
        phone: phone ?? "—",
        emailPro: emailPro ?? "—",
      });
    }
    this._replaceChildren(this.coordonneesSummaryTarget, fragment);
  }

  _restoreIdentiteInputs() {
    if (this.hasLegalNameInputTarget) this.legalNameInputTarget.value = this.state.identity.legalName ?? "";
    if (this.hasCommercialNameInputTarget) this.commercialNameInputTarget.value = this.state.identity.commercialName ?? "";
    if (this.hasRcsCityInputTarget) this.rcsCityInputTarget.value = this.state.identity.rcsCity ?? "";
    if (this.hasRmDepartmentInputTarget) this.rmDepartmentInputTarget.value = this.state.identity.rmDepartment ?? "";
  }

  _restoreCoordonneesInputs() {
    if (this.hasAddressLine1InputTarget) this.addressLine1InputTarget.value = this.state.contact.addressLine1 ?? "";
    if (this.hasAddressPostalCodeInputTarget) this.addressPostalCodeInputTarget.value = this.state.contact.addressPostalCode ?? "";
    if (this.hasAddressCityInputTarget) this.addressCityInputTarget.value = this.state.contact.addressCity ?? "";
    if (this.hasPhoneInputTarget) this.phoneInputTarget.value = this.state.contact.phone ?? "";
    if (this.hasEmailProInputTarget) this.emailProInputTarget.value = this.state.contact.emailPro ?? "";
    if (this.hasWebsiteUrlInputTarget) this.websiteUrlInputTarget.value = this.state.contact.websiteUrl ?? "";
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _readTrim(targetName) {
    const has = this[`has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`];
    if (!has) return "";
    const el = this[`${targetName}Target`];
    return (el && typeof el.value === "string") ? el.value.trim() : "";
  }

  _readNullable(targetName) {
    const trimmed = this._readTrim(targetName);
    return trimmed === "" ? null : trimmed;
  }

  _showError(block, message) {
    const targetName = block === "identite" ? "identiteErrorTarget" : "coordonneesErrorTarget";
    const hasTarget = block === "identite" ? this.hasIdentiteErrorTarget : this.hasCoordonneesErrorTarget;
    if (!hasTarget) {
      this.showError(message);
      return;
    }
    const el = this[targetName];
    el.textContent = message;
    el.hidden = false;
  }

  _hideError(block) {
    const targetName = block === "identite" ? "identiteErrorTarget" : "coordonneesErrorTarget";
    const hasTarget = block === "identite" ? this.hasIdentiteErrorTarget : this.hasCoordonneesErrorTarget;
    if (!hasTarget) return;
    const el = this[targetName];
    el.textContent = "";
    el.hidden = true;
  }

  _open(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _close(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  _replaceChildren(target, fragment) {
    while (target.firstChild) target.removeChild(target.firstChild);
    target.appendChild(fragment);
  }
}
